import { Injectable } from '@angular/core';
import { BusinessProcessNodeType } from '../models/business-process.model';
export interface BusinessProcessNodeSize {
  width: number;
  height: number;
}
export interface BusinessProcessTextArea extends BusinessProcessNodeSize {
  offsetX: number;
  offsetY: number;
}
interface Config {
  minimumWidth: number;
  maximumWidth: number;
  minimumHeight: number;
  maximumHeight: number;
  horizontalPadding: number;
  verticalPadding: number;
  characterWidth: number;
  lineHeight: number;
  textOffsetX: number;
  textOffsetY: number;
}
@Injectable({ providedIn: 'root' })
export class BusinessProcessNodeSizingService {
  private readonly c: Record<BusinessProcessNodeType, Config> = {
    action: {
      minimumWidth: 150,
      maximumWidth: 280,
      minimumHeight: 64,
      maximumHeight: 130,
      horizontalPadding: 46,
      verticalPadding: 22,
      characterWidth: 7,
      lineHeight: 18,
      textOffsetX: 6,
      textOffsetY: 0,
    },
    output: {
      minimumWidth: 150,
      maximumWidth: 260,
      minimumHeight: 68,
      maximumHeight: 130,
      horizontalPadding: 58,
      verticalPadding: 24,
      characterWidth: 7,
      lineHeight: 18,
      textOffsetX: 0,
      textOffsetY: 0,
    },
    outcome: {
      minimumWidth: 115,
      maximumWidth: 175,
      minimumHeight: 115,
      maximumHeight: 175,
      horizontalPadding: 48,
      verticalPadding: 48,
      characterWidth: 6.5,
      lineHeight: 17,
      textOffsetX: 0,
      textOffsetY: 0,
    },
    trigger: {
      minimumWidth: 150,
      maximumWidth: 260,
      minimumHeight: 68,
      maximumHeight: 125,
      horizontalPadding: 58,
      verticalPadding: 26,
      characterWidth: 7,
      lineHeight: 18,
      textOffsetX: 0,
      textOffsetY: 3,
    },
    decision: {
      minimumWidth: 145,
      maximumWidth: 230,
      minimumHeight: 95,
      maximumHeight: 160,
      horizontalPadding: 82,
      verticalPadding: 46,
      characterWidth: 7,
      lineHeight: 18,
      textOffsetX: 0,
      textOffsetY: 0,
    },
    model: {
      minimumWidth: 170,
      maximumWidth: 280,
      minimumHeight: 72,
      maximumHeight: 130,
      horizontalPadding: 50,
      verticalPadding: 34,
      characterWidth: 7.5,
      lineHeight: 18,
      textOffsetX: 0,
      textOffsetY: 3,
    },
  };
  getSize(
    type: BusinessProcessNodeType,
    text: string,
  ): BusinessProcessNodeSize {
    const c = this.c[type];
    let w = this.clamp(
      text.trim().length * c.characterWidth + c.horizontalPadding,
      c.minimumWidth,
      c.maximumWidth,
    );
    const lines = this.lines(text, w - c.horizontalPadding, c.characterWidth);
    let h = this.clamp(
      lines * c.lineHeight + c.verticalPadding,
      c.minimumHeight,
      c.maximumHeight,
    );
    if (type === 'outcome') {
      const s = this.clamp(Math.max(w, h), c.minimumWidth, c.maximumWidth);
      return { width: s, height: s };
    }
    return { width: w, height: h };
  }
  getTextArea(
    type: BusinessProcessNodeType,
    size: BusinessProcessNodeSize,
  ): BusinessProcessTextArea {
    const c = this.c[type];
    return {
      width: Math.max(20, size.width - c.horizontalPadding),
      height: Math.max(20, size.height - c.verticalPadding),
      offsetX: c.textOffsetX,
      offsetY: c.textOffsetY,
    };
  }
  private lines(text: string, width: number, cw: number) {
    const cap = Math.max(1, Math.floor(width / cw));
    const words = text.trim().split(/\s+/);
    let lines = 1,
      n = 0;
    for (const word of words) {
      const required = n ? word.length + 1 : word.length;
      if (n + required > cap) {
        lines++;
        n = word.length;
      } else n += required;
    }
    return lines;
  }
  private clamp(v: number, min: number, max: number) {
    return Math.max(min, Math.min(max, v));
  }
}
